import { createErrorToast, createSuccessToast } from "@/services/notification/ToastService";
import { strings } from "@/services/translation/strings";
import { CombinedError, OperationResult } from "urql";

export const toastSuccess = (message?: string) => {
  createSuccessToast(message || strings.common.success);
};

export const toastError = (text?: string) => {
  createErrorToast(text || strings.common.error);
};

export const toastMessage = (text: string) => {
  createErrorToast(text);
};

export interface MutationResultHandlerOptions<TData = unknown> {
  successMessage?: string;
  errorMessage?: string;
  onSuccess?: (data: TData | undefined) => void | Promise<void>;
  onError?: (error: CombinedError) => void | Promise<void>;
  suppressToast?: boolean;
  throwOnError?: boolean;
  /**
   * Optional validation for mutations whose GraphQL field is nullable.
   * Some backend mutations can return null data without an OperationResult.error.
   * Return false to treat the result as a failed mutation and show the error toast.
   */
  customSuccessCheck?: (data: TData | undefined) => boolean;
}

const defaultSuccessCheck = (data: unknown): boolean => {
  if (data === undefined || data === null) {
    return false;
  }

  if (typeof data !== 'object') {
    return Boolean(data);
  }

  const values = Object.values(data as Record<string, unknown>);
  if (values.length === 0) {
    return false;
  }

  return values.some(value => value !== undefined && value !== null && value !== false);
};

export const handleMutationResult = async <TData = unknown,>(
  response: OperationResult<TData> | undefined | null,
  options: MutationResultHandlerOptions<TData> = {}
) => {
  if (!response) {
    if (!options.suppressToast) {
      toastError(options.errorMessage);
    }

    return false;
  }

  if (response.error) {
    if (!options.suppressToast) {
      toastError(options.errorMessage ?? response.error.graphQLErrors?.[0]?.message ?? response.error.message);
    }

    await options.onError?.(response.error);

    if (options.throwOnError) {
      throw response.error;
    }

    return false;
  }

  const isSuccessful = options.customSuccessCheck
    ? options.customSuccessCheck(response.data)
    : defaultSuccessCheck(response.data);

  if (!isSuccessful) {
    if (!options.suppressToast) {
      toastError(options.errorMessage);
    }

    return false;
  }

  await options.onSuccess?.(response.data);

  if (!options.suppressToast && options.successMessage !== undefined) {
    toastSuccess(options.successMessage);
  }

  return true;
};

export const handleMutation = async <TData = unknown,>(
  execute: () => Promise<OperationResult<TData>>,
  options: MutationResultHandlerOptions<TData> = {}
): Promise<boolean> => {
  try {
    const response = await execute();
    return handleMutationResult(response, options);
  } catch (error) {
    const mutationError = error as CombinedError;

    if (!options.suppressToast) {
      toastError(options.errorMessage ?? (error instanceof Error ? error.message : undefined));
    }

    await options.onError?.(mutationError);

    if (options.throwOnError) {
      throw error;
    }

    return false;
  }
};

export const showToastMessage = (
  response: OperationResult,
  response2?: OperationResult,
  response3?: OperationResult
) => {
  if (response?.error || response2?.error || response3?.error) {
    toastError(response?.error?.message || response2?.error?.message || response3?.error?.message);
  } else {
    toastSuccess();
  }
};